import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Phone, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { CONTACT } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL;

const WELCOME = {
  role: "assistant",
  content:
    "Hi there — welcome to New Beginnings. I can answer questions about our program, insurance (including GreenShield), and admission. How can I help you today?",
};

const QUICK_REPLIES = [
  "Do you accept GreenShield?",
  "How quickly can someone be admitted?",
  "What does a typical day look like?",
];

const getSessionId = () => {
  let sid = localStorage.getItem("nb_chat_sid");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("nb_chat_sid", sid);
  }
  return sid;
};

export const ChatWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  // Restore prior conversation when the widget is first opened
  useEffect(() => {
    if (!open || historyLoaded) return;
    setHistoryLoaded(true);
    fetch(`${API}/api/chat/history/${sessionId.current}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.messages?.length) setMessages(data.messages);
      })
      .catch(() => {});
  }, [open, historyLoaded]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let sse = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        sse += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = sse.indexOf("\n\n")) !== -1) {
          const line = sse.slice(0, idx).trim();
          sse = sse.slice(idx + 2);
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            const delta = payload.delta;
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, content: last.content + delta };
              return next;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: `I'm sorry, I'm having trouble connecting right now. Please call us at ${CONTACT.phone} — our team is here 24/7.`,
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  };

  if (pathname.startsWith("/admin")) return null;

  return (
    <>
      {/* Launcher */}
      <button
        data-testid="chat-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Chat with us"}
        className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-[70] w-14 h-14 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white shadow-[0_12px_30px_-8px_rgba(194,124,94,0.6)] grid place-items-center transition-transform hover:scale-105 active:scale-95"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-40 right-4 md:bottom-24 md:right-6 z-[70] w-[calc(100vw-2rem)] max-w-[380px] h-[60vh] max-h-[540px] rounded-3xl bg-sand border border-line shadow-[0_30px_60px_-15px_rgba(0,0,0,0.3)] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-forest text-sand px-5 py-4 flex items-center gap-3 shrink-0">
            <span className="w-9 h-9 rounded-full overflow-hidden shrink-0 ring-2 ring-sand/30">
              <LogoMark className="w-full h-full" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-serif text-base leading-tight">New Beginnings Assistant</p>
              <p className="text-[11px] text-sage flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Online — replies instantly
              </p>
            </div>
            <a href={CONTACT.phoneHref} aria-label="Call us" className="p-2 rounded-full hover:bg-white/10 transition-colors">
              <Phone className="w-4 h-4" />
            </a>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={
                    m.role === "user"
                      ? "max-w-[85%] rounded-2xl rounded-br-md bg-forest text-sand px-4 py-2.5 text-sm leading-relaxed"
                      : "max-w-[85%] rounded-2xl rounded-bl-md bg-white border border-line text-ink px-4 py-2.5 text-sm leading-relaxed"
                  }
                >
                  {m.content}
                  {streaming && i === messages.length - 1 && m.role === "assistant" && !m.content && (
                    <Loader2 className="w-4 h-4 animate-spin text-terracotta" />
                  )}
                </div>
              </div>
            ))}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK_REPLIES.map((q, i) => (
                  <button
                    key={q}
                    data-testid={`chat-quick-${i}`}
                    onClick={() => send(q)}
                    className="text-xs rounded-full border border-terracotta/40 text-terracotta px-3 py-1.5 hover:bg-terracotta hover:text-white transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            className="shrink-0 border-t border-line bg-white p-3 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              maxLength={1000}
              className="flex-1 min-w-0 bg-sand rounded-full px-4 py-2.5 text-sm outline-none border border-transparent focus:border-terracotta/50"
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={streaming || !input.trim()}
              aria-label="Send message"
              className="w-10 h-10 rounded-full bg-terracotta hover:bg-[#b45a3d] disabled:opacity-40 text-white grid place-items-center transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <p className="bg-white text-center text-[10px] text-ink2/70 pb-2 shrink-0">
            In crisis? Call 911 or Talk Suicide Canada 1-833-456-4566
          </p>
        </div>
      )}
    </>
  );
};
